/**
 * onboardingStateBridge — module-level handoff between the imperative
 * `onboardingStateAdapter` (synchronous; called from AtlasProvider callbacks)
 * and the React-level `OnboardingStateProvider` (async; owns the server round-trip).
 *
 * Why a bridge: Atlas's adapter needs to stay synchronous so step transitions
 * are snappy (no awaited PATCH inside `advanceMissionStep`). The bridge lets
 * the provider register its async mutators on mount; the adapter then "fires
 * and forgets" a publish call whenever it writes to localStorage. The local
 * read path keeps using `localStorage` for instant responses; Realtime + the
 * provider's GET on window focus reconcile cross-user state.
 *
 * Scopes:
 *   - 'org-product' for mission step machines / feature-coach dismissals / checkpoints
 *   - 'org'         for org-wide flags (suppressAll)
 *   - 'user'        for personal "Restart mission" overrides
 *
 * When no provider is mounted (tests, storybook, very early boot before the
 * layout tree renders), the bridge silently no-ops. Writes still succeed
 * locally because the adapter continues to call the legacy localStorage
 * helpers; the server is just not told until the provider attaches.
 */

import type { MissionStepValue, OnboardingScope } from './OnboardingStateProvider';

type BridgeApi = {
  setMissionStep: (machineKey: string, step: string, scope: OnboardingScope) => Promise<void>;
  setFlag: (key: string, scope: OnboardingScope) => Promise<void>;
  clearUserOverride: () => Promise<void>;
  resetCanonical: (scope?: 'org' | 'org-product') => Promise<void>;
  getMissionStep: (machineKey: string) => MissionStepValue | undefined;
};

let currentApi: BridgeApi | null = null;

/** Provider registers itself on mount. Returns an unsubscribe. */
export function registerOnboardingBridge(api: BridgeApi): () => void {
  currentApi = api;
  return () => {
    if (currentApi === api) currentApi = null;
  };
}

/**
 * Fire-and-forget: publish a mission-step transition to the server. Synchronous
 * from the caller's POV (returns immediately). Errors are logged inside the
 * provider's `doPatch`.
 */
export function publishMissionStep(
  machineKey: string,
  step: string,
  scope: OnboardingScope = 'org-product'
): void {
  const api = currentApi;
  if (!api) return;
  void api.setMissionStep(machineKey, step, scope);
}

/** Fire-and-forget: publish a boolean flag (checkpoint / coach dismissal). */
export function publishFlag(key: string, scope: OnboardingScope = 'org-product'): void {
  const api = currentApi;
  if (!api) return;
  void api.setFlag(key, scope);
}

/** Personal restart — clears this user's overlay (no-op if provider absent). */
export function publishClearUserOverride(): void {
  const api = currentApi;
  if (!api) return;
  void api.clearUserOverride();
}

/** Read mission step from the provider snapshot (null when provider absent). */
export function readBridgedMissionStep(machineKey: string): MissionStepValue | undefined {
  const api = currentApi;
  if (!api) return undefined;
  return api.getMissionStep(machineKey);
}
